import elasticsearch from 'elasticsearch'
import Constants from 'src/utilities/constants'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

export function esClient(optionalCredentials) {
  return new elasticsearch.Client({ host: getUriMaybeWithCreds(optionalCredentials) })
}

export async function esSearch(esCreds, searchBody) {
  return await esClient(esCreds).search({
    index: 'ptrac',
    body: searchBody
  })
}

function getUriMaybeWithCreds(optionalCredentials) {
  let uri = process.env.VUE_APP_ELASTICSEARCH_URI
  if (optionalCredentials) {
    let protocol = 'https://'
    if (Constants.ENVIRONMENTS.DEVELOPMENT === process.env.NODE_ENV) {
      protocol = 'http://'
    }
    // in staging and production the url looks like https://un:pw@host.com
    uri = uri.replace(protocol, protocol + optionalCredentials + '@')
  }
  return uri
}

export function basicSearchBuilder(searchFields, searchTerm, hyphenFields, metadataFields) {
  let searchArray = []

  searchArray.push(multiMatch(searchFields, searchTerm))
  searchArray.push(childQueryBuilder('metadata', searchTerm, metadataFields))

  searchTerm.forEach(term => {
    if (termHasHyphen(term)) {
      searchArray.push(simpleQueryStringBuilder(hyphenFields, term))
    }
  })
  return searchArray
}

export function advancedSearchBuilder(searchFields, stateQuery) {
  let searchArray = []
  for (let [key, value] of Object.entries(stateQuery)) {
    let query = {
      match: {}
    }
    if (!!value) {
      searchFields.forEach(field => {
        if (field.includes(key)) {
          if (termHasHyphen(value)) {
            searchArray.push(hyphenQueryBuilder([field], value))
          } else {
            query.match[field + '.keyword'] = value
            searchArray.push(query)
          }
        }
      })
    }
  }
  return searchArray
}

function termHasHyphen(term) {
  return !!term.includes('-')
}

export function simpleQueryStringBuilder(fields, term) {
  const query = {
    simple_query_string: {
      query: '',
      fields: fields,
      default_operator: 'or',
    }
  }
  if (termHasHyphen(term)) {
    query.simple_query_string.query = '*' + term.split("-").join(" AND ") + '*'
  } else {
    query.simple_query_string.query = term
  }
  return query
}

export function hyphenQueryBuilder(fields, term) {
  return {
    simple_query_string: {
      query: term.split("-").join(" AND "),
      fields: fields,
      default_operator: 'or'
    }
  }
}

export function multiMatch(fields, searchTerms) {
  return {
    multi_match: {
      query: searchTerms.join(' '),
      fields: fields,
      operator: 'and'
    }
  }
}

export function dateBetween(datePair, field) {
  const gte = dayjs(datePair[0]).startOf('day')
  const lte = dayjs(datePair[1]).endOf('day')

  return {
    range: {
      [field]: {
        gte,
        lte
      }
    }
  }
}

export function filterBuilder(filterFields, stateQuery) {
  let filters = []

  filterFields.forEach(element => {
    if (stateQuery[element] != null && stateQuery[element] != '') {
      let criteria = {match: {}}
      criteria.match[element + '.keyword'] = stateQuery[element]
      filters.push(criteria)
    }
  })
  return filters
}

export function planNameFilterBuilder(filterFields, stateQuery) {
  let filters = []

  filterFields.forEach(element => {
    if (stateQuery[element] != null) {
      let criteria = {match: {}}
      criteria.match[element] = stateQuery[element]
      filters.push(criteria)
    }
  })
  return filters
}

export function exactMetadataQueryBuilder(type, searchTerm) {
  let query = {
    has_child: {
      type: type,
      query: {
        bool: {
          should: [],
          minimum_should_match: 1
        }
      }
    }
  }

  searchTerm.split(' ').forEach(term => {
    query.has_child.query.bool.should.push(
      {
        term:
          {'value.keyword': term}
      },
      {
        term:
          {'name.keyword': term}
      },
      {
        match:
          {'value': term}
      },
      {
        match:
          {'name': term}
      })
  })
  return query
}

function childQueryBuilder(type, searchTerm, metadataFields) {
  let query = {
    has_child: {
      type: type,
      query: {
        bool: {
          should: []
        }
      }
    }
  }

  searchTerm.forEach(term => {
    query.has_child.query.bool.should.push(simpleQueryStringBuilder(metadataFields, term))
  })

  return query
}

export function parentQueryBuilder(parentType, field, term) {
  return {
    has_parent: {
      parent_type: parentType,
      query: {
        match: {
          [field]: term
        }
      }
    }
  }
}

export function invoiceClientBuilder(clientIds) {
  const query = {
    bool: {
      should: [],
      minimum_should_match : 1
    }
  }

  clientIds.forEach((clientId) => {
    query.bool.should.push(
      {
        has_parent: {
          parent_type: Constants.ENTITY_FIELDS.JOB.JOBS,
          query: {
            match: {
              clientId: clientId
            }
          }
        }
      },
    )
  })

  return query
}

export function parentQuery(parentType, fields, stateQuery) {
  return {
    has_parent: {
      parent_type: parentType,
      query: multiMatch(fields, stateQuery)
    }
  }
}

export function doubleParentQuery(parentType, grandParentType, fields, stateQuery) {
  return {
    has_parent: {
      parent_type: parentType,
      query: {
        has_parent: {
          parent_type: grandParentType,
          query: multiMatch(fields, stateQuery)
        }
      }
    }
  }
}

export function parentCompanyQuery(parentType, stateQuery) {
  let query = {
    has_parent: {
      parent_type: parentType,
      query: {
        bool: {
          must: []
        }
      }
    }
  }
  if (stateQuery.clientId) {
    query.has_parent.query.bool.must.push({match: {clientId: stateQuery.clientId}})
  }
  if (stateQuery.builderId) {
    query.has_parent.query.bool.must.push({match: {builderId: stateQuery.builderId}})
  }

  return query
}

export function parentCommunityQuery(parentType, stateQuery) {
  let query = {
    has_parent: {
      parent_type: parentType,
      query: {
        bool: {
          must: []
        }
      }
    }
  }
  if (stateQuery.communityId) {
    query.has_parent.query.bool.must.push({match: {"community._id": stateQuery.communityId}})
  }
  return query
}

export function booleanTrueQuery(field, stateQuery) {
  stateQuery.query.bool.must.push({
    match: {
      [field]: "true"
    }
  })
}
